import type { DictionaryKey } from '@dia/i18n';
import type { Floor, House, Room, RoomClaim, Guest } from '@dia/db';

/**
 * The pure parts of the guest room picker. Shared arithmetic (how full a room
 * is, the deadline, the bed line) lives in @dia/db because the ops grid needs
 * the same answers. What is here is what only the guest screen cares about:
 * how the list is grouped, what a refusal means in plain words, and who in the
 * party is still without a bed.
 */

/** One heading in the list: a house, and a level inside it where there is one. */
export type RoomGroup = {
  key: string;
  houseSlug: string;
  houseName: { es: string; en: string };
  floor: Floor | null;
  rooms: Room[];
};

/**
 * The eight bedrooms, in the order a person walks them: house by house in seed
 * order, and inside a house by level. A house with one level gets no level
 * heading, because "Main level" over a two room list is noise.
 */
export function groupRooms(rooms: Room[], houses: House[]): RoomGroup[] {
  const claimable = rooms
    .filter((r) => r.claimable)
    .sort((a, b) => a.sort - b.sort || a.slug.localeCompare(b.slug));

  const groups: RoomGroup[] = [];

  for (const house of [...houses].sort((a, b) => a.sort - b.sort)) {
    const mine = claimable.filter((r) => r.house_id === house.id);
    if (mine.length === 0) continue;

    const floors = [...new Set(mine.map((r) => r.floor))];
    const split = floors.length > 1;

    for (const floor of floors) {
      const inFloor = mine.filter((r) => r.floor === floor);
      groups.push({
        key: `${house.slug}:${floor ?? 'all'}`,
        houseSlug: house.slug,
        houseName: { es: house.name_es, en: house.name_en },
        floor: split ? floor : null,
        rooms: inFloor,
      });
    }
  }

  return groups;
}

/**
 * What the island just refused, in words a guest can act on.
 *
 * The messages come from migration 016's trigger and from row level security,
 * so they are matched rather than parsed. Anything unrecognised falls back to
 * the plain "try again", never to a raw Postgres string on a guest's screen.
 */
export function claimErrorKey(message: string): DictionaryKey {
  const text = message.toLowerCase();
  if (text.includes('room is full')) return 'guest.rooms.error_full';
  if (text.includes('time to pick')) return 'guest.rooms.error_deadline';
  if (text.includes('row-level security') || text.includes('row level security')) {
    return 'guest.rooms.error_not_yours';
  }
  if (text.includes('duplicate key') || text.includes('unique')) return 'guest.rooms.error_full';
  if (text === 'needs_signal') return 'guest.rooms.offline';
  return 'guest.rooms.error_generic';
}

/** Who in the party still has no bed, in the order the names were added. */
export function unplaced(party: Guest[], claims: Array<Pick<RoomClaim, 'guest_id'>>): Guest[] {
  const placed = new Set(claims.map((c) => c.guest_id));
  return party.filter((g) => !placed.has(g.id));
}

/** The people in one room, with the person reading the screen listed first. */
export function occupantsOf(
  roomId: string,
  claims: RoomClaim[],
  party: Guest[],
  meId: string | null,
): Array<{ claim: RoomClaim; guest: Guest | null; isMe: boolean }> {
  return claims
    .filter((c) => c.room_id === roomId)
    .map((claim) => ({
      claim,
      guest: party.find((g) => g.id === claim.guest_id) ?? null,
      isMe: claim.guest_id === meId,
    }))
    .sort((a, b) => {
      if (a.isMe !== b.isMe) return a.isMe ? -1 : 1;
      return a.claim.created_at.localeCompare(b.claim.created_at);
    });
}

/** The claim belonging to the person reading, if they have one. */
export function myClaim(claims: RoomClaim[], meId: string | null): RoomClaim | null {
  return claims.find((c) => c.guest_id === meId) ?? null;
}
